import type { ApplicationSummary } from '../components/common/summary-common/applicationSummary';

const ANSWERED_STATUS = 'answered';

const hasMeaningfulValue = (value: unknown): boolean => {
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return value !== null && value !== undefined;
};

export const hasPopulatedAnswers = (page: ApplicationSummary): boolean =>
  page.json?.sections?.some(section =>
    section.groups?.some(group =>
      group.fields?.some(field => hasMeaningfulValue(field.value)),
    ),
  ) ?? false;

const pagePriority = (page: ApplicationSummary): number => {
  const populated = hasPopulatedAnswers(page);
  const answered = page.json?.status?.trim().toLowerCase() === ANSWERED_STATUS;

  if (answered && populated) return 3;
  if (populated) return 2;
  if (answered) return 1;
  return 0;
};

export const selectPreferredApplicationSummaryPage = (
  current: ApplicationSummary,
  candidate: ApplicationSummary,
): ApplicationSummary => {
  const currentPriority = pagePriority(current);
  const candidatePriority = pagePriority(candidate);

  if (candidatePriority !== currentPriority) {
    return candidatePriority > currentPriority ? candidate : current;
  }

  return candidate.id > current.id ? candidate : current;
};

/**
 * The legacy API can return more than one row for a template page. Collapse those rows once,
 * before any summary mapper/cache reads them: prefer an answered page with user values, then
 * any populated page, and finally the newest row id.
 */
export const selectPreferredApplicationSummaryPages = (
  pages: ApplicationSummary[],
): ApplicationSummary[] => {
  const preferredByTemplate = new Map<string, ApplicationSummary>();

  for (const page of pages) {
    const current = preferredByTemplate.get(page.template_page_id);
    preferredByTemplate.set(
      page.template_page_id,
      current ? selectPreferredApplicationSummaryPage(current, page) : page,
    );
  }

  return Array.from(preferredByTemplate.values());
};
